import type { WorkbenchPreviewCrash } from '../stores/workbench';

/**
 * Turns the light preview's compile errors into the payload the existing "Fix with AI" path
 * already understands (`WORKBENCH_FIX_PREVIEW_CRASH_EVENT` → `buildPreviewCrashFixPrompt`), so a
 * compile failure reaches the model the same way a runtime crash does.
 *
 * Light-preview plan F2.
 */

/** esbuild reports `src/App.tsx:12:3: Unexpected }`; the prompt shows the file separately. */
const LOCATION = /^([^\s:]+\.[a-z]+):(\d+):(\d+):\s*/i;

const MAX_ERRORS_IN_PROMPT = 5;

export function fileFromCompileErrors(errors: readonly string[]): string | undefined {
  for (const error of errors) {
    const match = LOCATION.exec(error.trim());
    if (match) return match[1];
  }
  return undefined;
}

export function compileErrorsToCrash(
  errors: readonly string[],
  pathname = '/',
): WorkbenchPreviewCrash | null {
  const listed = errors.map((error) => error.trim()).filter(Boolean);
  if (listed.length === 0) return null;

  const shown = listed.slice(0, MAX_ERRORS_IN_PROMPT);
  const remaining = listed.length - shown.length;
  const message = remaining > 0 ? [...shown, `…and ${remaining} more`].join('\n') : shown.join('\n');

  return {
    kind: 'compile',
    message,
    file: fileFromCompileErrors(listed),
    pathname,
    at: Date.now(),
  };
}

/**
 * What to tell the founder when generated code imports a package the preview cannot serve. The
 * prototype is still shown: only that import is missing, and the server build remains the check
 * that decides whether the version is good.
 */
export function unsupportedPackagesMessage(packages: readonly string[]): string {
  if (packages.length === 0) return '';
  const names = packages.map((name) => `“${name}”`);
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return names.length === 1
    ? `${list} is not available in the preview yet, so parts of this screen may be missing.`
    : `${list} are not available in the preview yet, so parts of this screen may be missing.`;
}
